import React from "react";
import {
    Box,
    Card,
    CardMedia,
    CardContent,
    Typography,
    Button,
    IconButton,
    Chip,
    Stack,
    Tooltip
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { useNavigate } from "react-router-dom";
import { getProductCategoriaLabel } from "../utils/productCategories";

// --- HELPERS ---
const formatCurrency = (val) => {
    if (!val) return "R$ 0,00";
    // Se vier como string "1.200,00", tentamos limpar
    const str = val.toString().replace("R$", "").trim();
    // Se for apenas número ou string limpa
    return `R$ ${str}`;
};

// Calcula % de desconto
const getDiscountPercent = (normal, promo) => {
    const n = parseFloat(normal.toString().replace(/[^\d,.]/g, "").replace(",", "."));
    const p = parseFloat(promo.toString().replace(/[^\d,.]/g, "").replace(",", "."));
    if (!n || !p || n <= p) return null;
    return Math.round(((n - p) / n) * 100);
};

// --- STYLED COMPONENTS ---
const StyledCard = styled(Card)(({ theme }) => ({
    height: "100%", // Ocupa toda altura do Grid
    display: "flex",
    flexDirection: "column",
    position: "relative",
    borderRadius: "16px",
    padding: 5,
    boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
    transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
    border: "1px solid rgba(0,0,0,0.05)",
    overflow: "hidden",
    cursor: "pointer",

    "&:hover": {
        transform: "translateY(-5px)",
        boxShadow: "0 12px 24px rgba(0,0,0,0.1)",
        borderColor: "transparent",

        "& .product-image": {
            transform: "scale(1.08)",
        },
        "& .action-buttons": {
            opacity: 1,
            transform: "translateY(0)",
        }
    },
}));

const ImageWrapper = styled(Box)(({ theme }) => ({
    position: "relative",
    overflow: "hidden",
    height: 220,
    width: "100%",
    padding: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
    [theme.breakpoints.down("sm")]: {
        height: 180,
        padding: "12px",
    },
}));

const StyledImage = styled("img")(({ theme }) => ({
    width: "100%",
    height: "100%",
    objectFit: "contain",
    maxWidth: "160px",
    maxHeight: "180px",
    transition: "transform 0.4s ease",
    [theme.breakpoints.down("sm")]: {
        maxWidth: "120px",
        maxHeight: "140px",
    },
}));

export default function ProductCard({ product }) {
    const navigate = useNavigate();

    const {
        id,
        nome,
        imagens,
        precos,
        descricao,
    } = product;

    const image = imagens?.[0] || "/placeholder.png";
    const isPromo = precos?.isPromo;
    const priceNormal = formatCurrency(precos?.normal);
    const pricePromo = formatCurrency(precos?.promo);

    // Cálculo visual do desconto
    const discountPercent = isPromo ? getDiscountPercent(precos?.normal, precos?.promo) : null;

    const openProductPage = () => {
        navigate(`/produto/${id}`);
    };

    const handleAddToCart = (e) => {
        e.stopPropagation();
        console.log("Adicionar ao carrinho:", id);
        // Lógica de adicionar ao contexto de carrinho aqui
    };

    const truncateText = (text, limit = 40) => {
        if (!text) return "";
        return text.length > limit
            ? text.slice(0, limit).trim() + "..."
            : text;
    };

    return (
        <StyledCard onClick={openProductPage} sx={{ width: { md: 260, xs: 300 } }}>

            {/* TAGS FLUTUANTES */}
            <Box sx={{ position: "absolute", top: 12, left: 12, zIndex: 2, display: "flex", gap: 1 }}>
                {isPromo && (
                    <Chip
                        label={discountPercent ? `-${discountPercent}%` : "OFERTA"}
                        size="small"
                        sx={{
                            bgcolor: "var(--azul, #1976d2)", // Fallback se a variavel nao existir
                            color: "white",
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            height: 24,
                        }}
                    />
                )}
            </Box>

            {/* IMAGEM */}
            <ImageWrapper>
                <StyledImage
                    src={image}
                    alt={nome}
                    className="product-image"
                    loading="lazy"
                />
            </ImageWrapper>

            {/* CONTEÚDO */}
            <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", pt: 1, pb: 2 }}>

                {/* CATEGORIA (Opcional - se tiver no obj product) */}
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    {getProductCategoriaLabel(product) || "Geral"}
                </Typography>

                {/* TÍTULO (Limitado a 2 linhas) */}
                <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{
                        lineHeight: 1.3,
                        mb: 0,
                        minHeight: 28, // Altura fixa para 2 linhas
                        display: '-webkit-box',
                        overflow: 'hidden',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                    }}
                >
                    {nome}
                </Typography>

                {/* descição limitada a 2 linhas */}
                <Typography
                    variant="subtitle2"
                    fontWeight={500}
                >
                    {truncateText(descricao, 25)}
                </Typography>

                {/* PREÇO */}
                <Box sx={{ mt: "auto", mb: 2 }}>
                    {isPromo ? (
                        <Stack direction="row" alignItems="baseline" spacing={1}>
                            <Typography
                                variant="h6"
                                fontWeight={800}
                                color="var(--azul, #1976d2)"
                            >
                                {pricePromo}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ textDecoration: "line-through", color: "text.disabled" }}
                            >
                                {priceNormal}
                            </Typography>
                        </Stack>
                    ) : (
                        <Typography variant="h6" fontWeight={800} color="var(--azul, #1976d2)">
                            {priceNormal}
                        </Typography>
                    )}

                    {/* Parcelamento (Fictício/Visual para compor) */}
                    <Typography variant="caption" color="text.primary" display="block">
                        ou até 12x no cartão
                    </Typography>
                </Box>

                {/* AÇÕES */}
                <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                        variant="contained"
                        fullWidth
                        disableElevation
                        onClick={(e) => {
                            e.stopPropagation();
                            openProductPage();
                        }}
                        sx={{
                            bgcolor: "var(--azul, #1976d2)",
                            fontWeight: 700,
                            borderRadius: 2,
                            textTransform: "none",
                            py: 1,
                            "&:hover": { bgcolor: "var(--azul-escuro, #112040)" }
                        }}
                    >
                        Comprar
                    </Button>

                </Stack>

            </CardContent>
        </StyledCard>
    );
}